import { gql, useQuery } from '@apollo/client';
import { Divider, Link, Stack, Text } from '@chakra-ui/react';
import * as React from 'react';

import { NavigationButton } from '@/components/NavigationButton';
import { ECommunityAccessConfig } from '@/graphql-types/globalTypes';
import { useContactSupportLink } from '@/hooks/useContactSupportLink';
import {
  BookOpenIcon,
  HeadphonesIcon,
  HomeIcon,
  TagIcon,
} from '@/imports/ui/chakra/feather';
import { GroupIcon } from '@/imports/ui/components/GroupIcon';

import { VanillaNavigation_Query } from './graphql-types/VanillaNavigation_Query';

const QUERY = gql`
  query VanillaNavigation_Query {
    currentUser {
      _id
      canAccessCommunity
      canAccessCommunityReadOnly
      visibleGroupLinks {
        _id
        group {
          _id
          name
          profileImage {
            _id
            imageProxy(input: { h: 36, w: 36, fit: "fill", fill: "solid" })
          }
        }
      }
      ...useContactSupportLink_UserFragment
    }
    tenant {
      result {
        _id
        communityAccessConfig
        ...useContactSupportLink_TenantFragment
      }
    }
  }
  ${useContactSupportLink.userFragment}
  ${useContactSupportLink.tenantFragment}
`;

export const VanillaNavigation: React.FC = () => {
  const query = useQuery<VanillaNavigation_Query>(QUERY);

  const currentUser = query.data?.currentUser;
  const tenant = query.data?.tenant.result;
  const canAccessCommunity = currentUser
    ? currentUser.canAccessCommunity
    : tenant?.communityAccessConfig === ECommunityAccessConfig.PUBLIC;
  const canAccessCommunityReadOnly = currentUser?.canAccessCommunityReadOnly;

  const { contactSupportLink, contactSupportText } = useContactSupportLink(
    currentUser,
    tenant
  );

  return (
    <Stack>
      <Stack>
        {canAccessCommunity && (
          <NavigationButton href="/community" leftIcon={<HomeIcon />} isExact>
            Feed
          </NavigationButton>
        )}

        {canAccessCommunityReadOnly && (
          <NavigationButton
            href="/my-company/community"
            leftIcon={<HeadphonesIcon />}
          >
            Community Feed
          </NavigationButton>
        )}

        {canAccessCommunity && (
          <NavigationButton href="/community/tags" leftIcon={<TagIcon />}>
            Tags
          </NavigationButton>
        )}
      </Stack>

      {canAccessCommunity && (
        <>
          <Text px="4" py="2">
            Groups
          </Text>

          <NavigationButton
            href="/community/groups"
            leftIcon={<BookOpenIcon />}
            isExact
          >
            All Groups
          </NavigationButton>

          <>
            {currentUser?.visibleGroupLinks?.map(({ group }) => (
              <NavigationButton
                href={`/community/groups/${group._id}/activity`}
                matchHref={`/community/groups/${group._id}`}
                key={group._id}
                leftIcon={
                  <GroupIcon
                    name={group.name}
                    url={group.profileImage?.imageProxy || null}
                  />
                }
              >
                {group.name}
              </NavigationButton>
            ))}
          </>
        </>
      )}

      {currentUser && (
        <>
          <Divider />

          <Link href={contactSupportLink} isExternal px="4">
            {contactSupportText}
          </Link>
        </>
      )}
    </Stack>
  );
};
